<main class="mt-4xl pb-2xl">
  <div class="grid grid-cols-12 gap-lg">
    <!-- Requests per Status -->
    <div class="col-span-4">
      <div class="p-lg border border-neutral-200 rounded-xl">
        <div class="flex flex-col gap-lg">
          <div>
            <lib-range-filter-dropdown
              [selectedRangeFilter]="selectedRequestsPerStatusRangeFilter()"
              [rangeFilterOptions]="rangeFilterOptions()"
              (selectRangeFilter)="
                selectRequestsPerStatusRangeFilter($event)
              " />

            <div class="font-semibold text-neutral-700 text-sm mt-sm">
              {{ 'reports_tab_requests_per_status_label' | transloco }}
            </div>
          </div>

          <div class="requests-per-status-chart-container">
            <lib-charts-wrapper
              [type]="requestsPerStatusChart()?.type ?? 'doughnut'"
              [id]="'doughnut-requests-per-status'"
              [labels]="requestsPerStatusChart()?.data?.labels ?? []"
              [datasets]="requestsPerStatusChart()?.data?.datasets ?? []"
              [title]="requestsPerStatusChart()?.title ?? ''"
              [value]="requestsPerStatusChart()?.value ?? 0"
              [doughnutChartCutout]="requestsPerStatusChart()?.cutout ?? 0"
              [height]="requestsPerStatusChart()?.height ?? 160"
              [width]="requestsPerStatusChart()?.width ?? 160"
              [animation]="requestsPerStatusChartAnimation()" />
          </div>

          <div class="flex flex-col gap-sm">
            @for (
              item of requestsPerStatusChartItems();
              track i;
              let i = $index
            ) {
              <lib-charts-item [item]="item" [itemDotClass]="'rounded-md'" />
            }
          </div>
        </div>
      </div>
    </div>

    <!-- Average Completion Duration vs. Workflow Total SLA -->
    <div class="col-span-8">
      <div class="p-lg border border-neutral-200 rounded-xl">
        <div class="flex flex-col gap-lg">
          <div>
            <lib-range-filter-dropdown
              [selectedRangeFilter]="selectedCompletionDurationRangeFilter()"
              [rangeFilterOptions]="completionDurationRangeFilterOptions()"
              (selectRangeFilter)="
                selectCompletionDurationRangeFilter($event)
              " />

            <div class="font-semibold text-neutral-700 text-sm mt-sm">
              {{
                'reports_tab_average_completion_duration_vs_workflow_total_sla_label'
                  | transloco
              }}
            </div>
          </div>

          <!-- additional info -->
          <div class="flex items-center gap-2xl">
            <div>
              <div class="text-xs3 text-neutral-700 font-medium mb-x-sm">
                {{
                  'reports_tab_average_completion_duration_label' | transloco
                }}
              </div>

              <div class="text-base text-neutral-900 font-semibold">
                {{
                  averageCompletionDurationChartData()?.data?.additional_info?.[
                    'average_completion_duration'
                  ] ?? 0
                }}

                <span class="text-neutral-500">
                  {{ 'reports_tab_wh_label' | transloco }}
                </span>
              </div>
            </div>

            <div>
              <div class="text-xs3 text-neutral-700 font-medium mb-x-sm">
                {{ 'reports_tab_workflow_total_sla_label' | transloco }}
              </div>

              <div class="text-base text-neutral-900 font-semibold">
                {{
                  averageCompletionDurationChartData()?.data?.additional_info?.[
                    'workflow_total_sla'
                  ] ?? 0
                }}

                <span class="text-neutral-500">
                  {{ 'reports_tab_wh_label' | transloco }}
                </span>
              </div>
            </div>

            <div>
              <div class="text-xs3 text-neutral-700 font-medium mb-x-sm">
                {{ 'reports_tab_rate_label' | transloco }}
              </div>

              <div class="text-base text-neutral-900 font-semibold">
                {{
                  averageCompletionDurationChartData()?.data?.additional_info?.[
                    'rate'
                  ] ?? 0
                }}

                <span class="text-neutral-500">%</span>
              </div>
            </div>
          </div>

          <div class="average-completion-duration-chart-container">
            <lib-charts-wrapper
              [type]="averageCompletionDurationChart()?.type ?? 'line'"
              [id]="'line-average-completion-duration'"
              [labels]="averageCompletionDurationChart()?.data?.labels ?? []"
              [datasets]="
                averageCompletionDurationChart()?.data?.datasets ?? []
              "
              [title]="''"
              [value]="0"
              [doughnutChartCutout]="0"
              [height]="averageCompletionDurationChart()?.height ?? 160"
              [width]="averageCompletionDurationChart()?.width ?? 160"
              [animation]="averageCompletionDurationChartAnimation()" />
          </div>

          <div class="flex gap-2xl">
            @for (
              item of averageCompletionDurationChartItems();
              track i;
              let i = $index
            ) {
              <lib-charts-item [item]="item" [itemDotClass]="'rounded-full'" />
            }
          </div>
        </div>
      </div>
    </div>

    <!-- Current Pending Tasks per Assignee -->
    <div class="col-span-6">
      <div class="p-lg border border-neutral-200 rounded-xl">
        <div class="flex flex-col gap-lg">
          <div>
            <lib-range-filter-dropdown
              [selectedRangeFilter]="selectedCurrentPendingTasksRangeFilter()"
              [rangeFilterOptions]="rangeFilterOptions()"
              [hasToggleAction]="false" />

            <div class="font-semibold text-neutral-700 text-sm mt-sm">
              {{
                'reports_tab_current_pending_tasks_per_assignee_label'
                  | transloco
              }}
            </div>
          </div>

          <div class="flex items-center gap-xl">
            <div class="doughnut-chart-container">
              <lib-charts-wrapper
                [type]="
                  currentPendingTasksPerAssigneeChart()?.type ?? 'doughnut'
                "
                [id]="'doughnut-current-pending-tasks-per-assignee'"
                [labels]="
                  currentPendingTasksPerAssigneeChart()?.data?.labels ?? []
                "
                [datasets]="
                  currentPendingTasksPerAssigneeChart()?.data?.datasets ?? []
                "
                [title]="currentPendingTasksPerAssigneeChart()?.title ?? ''"
                [value]="currentPendingTasksPerAssigneeChart()?.value ?? 0"
                [doughnutChartCutout]="
                  currentPendingTasksPerAssigneeChart()?.cutout ?? 0
                "
                [height]="currentPendingTasksPerAssigneeChart()?.height ?? 160"
                [width]="currentPendingTasksPerAssigneeChart()?.width ?? 160"
                [animation]="currentPendingTasksPerAssigneeChartAnimation()" />
            </div>

            <div class="flex flex-col gap-sm flex-1">
              @for (
                item of currentPendingTasksPerAssigneeChartItems();
                track i;
                let i = $index
              ) {
                <lib-charts-item
                  [item]="item"
                  [showTooltip]="true"
                  [itemDotClass]="'rounded-md'" />
              }
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Completed Requests On-Time vs. Delayed -->
    <div class="col-span-6">
      <div class="p-lg border border-neutral-200 rounded-xl">
        <div class="flex flex-col gap-lg">
          <div>
            <lib-range-filter-dropdown
              [selectedRangeFilter]="
                selectedCompletedRequestsOnTimeVsDelayedRangeFilter()
              "
              [rangeFilterOptions]="rangeFilterOptions()"
              (selectRangeFilter)="
                selectCompletedRequestsOnTimeVsDelayedRangeFilter($event)
              " />

            <div class="font-semibold text-neutral-700 text-sm mt-sm">
              {{
                'reports_tab_completed_requests_on_time_vs_delayed_label'
                  | transloco
              }}
            </div>
          </div>

          <div class="flex items-center gap-xl">
            <div class="doughnut-chart-container">
              <lib-charts-wrapper
                [type]="
                  completedRequestsOnTimeVsDelayedChart()?.type ?? 'doughnut'
                "
                [id]="'doughnut-completed-requests-on-time-vs-delayed'"
                [labels]="
                  completedRequestsOnTimeVsDelayedChart()?.data?.labels ?? []
                "
                [datasets]="
                  completedRequestsOnTimeVsDelayedChart()?.data?.datasets ?? []
                "
                [title]="completedRequestsOnTimeVsDelayedChart()?.title ?? ''"
                [value]="completedRequestsOnTimeVsDelayedChart()?.value ?? 0"
                [doughnutChartCutout]="
                  completedRequestsOnTimeVsDelayedChart()?.cutout ?? 0
                "
                [height]="
                  completedRequestsOnTimeVsDelayedChart()?.height ?? 160
                "
                [width]="completedRequestsOnTimeVsDelayedChart()?.width ?? 160"
                [animation]="
                  completedRequestsOnTimeVsDelayedChartAnimation()
                " />
            </div>

            <div class="flex flex-col gap-sm flex-1">
              @for (
                item of completedRequestsOnTimeVsDelayedChartItems();
                track i;
                let i = $index
              ) {
                <lib-charts-item [item]="item" [itemDotClass]="'rounded-md'" />
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</main>
